import { EntityId, PrintServiceProductEntity } from "@jackfruit/common"
import { SagaIterator } from "redux-saga"
import { call, put, select } from "redux-saga/effects"
import { ImageEntity } from "~/interfaces/entities/Image"
import { ImageAITransformationInputEntity } from "~/interfaces/entities/ImageAITransformationInput"
import { ImageAITransformationOutputEntity } from "~/interfaces/entities/ImageAITransformationOutput"
import {
  ImageAITransformationResult,
  ImageAITransformationStatus,
} from "~/interfaces/entities/ImageAITransformationResult"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { LineItemEntity } from "~/interfaces/entities/LineItem"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { getDefaultImageTransformation } from "~/services/Utils"
import {
  imageAITransformationInputs,
  imageAITransformationInputsSelectors,
} from "../state/imageAITransformationInputs"
import {
  imageAITransformationOutputs,
  imageAITransformationOutputsSelectors,
} from "../state/imageAITransformationOutputs"
import { images } from "../state/images"
import { uploads } from "../state/uploads"
import { RootState } from "../store"
import { getImageRegion } from "./imageRegion"
import { getImage, getImageDimensionsFromUrl } from "./images"
import { getLineItem } from "./lineItems"
import { getPrintServiceProduct } from "./printServiceProducts"
import { getUpload } from "./uploads"
import { imageTransformations } from "../state/imageTransformations"
import { projectDimensions } from "~/services/cropHelpers"

export function* getImageAITransformationOutput(
  imageAITransformationOutputId: EntityId
): SagaIterator<ImageAITransformationOutputEntity> {
  const imageAITransformationOutput: ImageAITransformationOutputEntity =
    yield select((state: RootState) =>
      imageAITransformationOutputsSelectors.selectById(
        state,
        imageAITransformationOutputId
      )
    )

  return imageAITransformationOutput
}

export function* getImageAITransformationInput(
  imageAITransformationInputId: EntityId
): SagaIterator<ImageAITransformationInputEntity> {
  const imageAITransformationInput: ImageAITransformationInputEntity =
    yield select((state: RootState) =>
      imageAITransformationInputsSelectors.selectById(
        state,
        imageAITransformationInputId
      )
    )

  return imageAITransformationInput
}

export function* updateImageAITransformationStatus({
  imageAITransformationInputId,
  imageAITransformationOutputId,
  status,
}: {
  imageAITransformationInputId: EntityId
  imageAITransformationOutputId: EntityId
  status: ImageAITransformationStatus
}): SagaIterator<void> {
  const { updateOne: updateOneImageAITransformationInput } =
    imageAITransformationInputs.actions
  const { updateOne: updateOneImageAITransformationOutput } =
    imageAITransformationOutputs.actions

  yield put(
    updateOneImageAITransformationInput({
      id: imageAITransformationInputId,
      changes: { status },
    })
  )

  yield put(
    updateOneImageAITransformationOutput({
      id: imageAITransformationOutputId,
      changes: { status },
    })
  )
}

export function* applyAITransformationToUpload(payload: {
  imageAITransformationInputId: EntityId
  imageAITransformationOutputId: EntityId
  imageAITransformResult: ImageAITransformationResult
}): SagaIterator {
  const { imageAITransformResult, imageAITransformationOutputId } = payload

  const { updateOne: updateOneImageAITransformationOutput } =
    imageAITransformationOutputs.actions
  const { updateOne: updateOneImage } = images.actions
  const { updateOne: updateOneUpload } = uploads.actions
  const { updateOne: updateOneImageTransformation } =
    imageTransformations.actions

  const imageAITransformationOutput: ImageAITransformationOutputEntity =
    yield call(getImageAITransformationOutput, imageAITransformationOutputId)

  const lineItem: LineItemEntity = yield call(
    getLineItem,
    imageAITransformationOutput.lineItemId
  )

  const product: PrintServiceProductEntity = yield call(
    getPrintServiceProduct,
    lineItem.productId
  )

  const isClampingEnabled = Boolean(
    product?.metaData?.web?.editor?.clamp ?? true
  )
  const imageFit = product?.metaData?.web?.editor?.imageFit || "fill"

  const imageRegion: ImageRegionEntity = yield call(
    getImageRegion,
    imageAITransformationOutput.imageRegionId
  )

  // update image and upload
  const upload: UploadEntity = yield call(
    getUpload,
    imageAITransformationOutput.uploadId
  )
  const image: ImageEntity = yield call(getImage, upload.imageId)

  yield put(
    updateOneImageAITransformationOutput({
      id: imageAITransformationOutputId,
      changes: {
        imageUrl: imageAITransformResult.imageUrl,
        outputUrl: imageAITransformResult.outputUrl,
        presignedThumbnailUrl: imageAITransformResult.presignedThumbnailUrl,
      },
    })
  )

  const imageDimensions = yield call(
    getImageDimensionsFromUrl,
    imageAITransformResult.presignedThumbnailUrl
  )

  const newDimensions = imageDimensions

  yield put(
    updateOneImage({
      id: image.id,
      changes: {
        externalUrl: imageAITransformResult.outputUrl,
        localUrl: imageAITransformResult.presignedThumbnailUrl,
        height: newDimensions.height,
        width: newDimensions.width,
      },
    })
  )

  // reset transformation
  const newImageTransformation = getDefaultImageTransformation(
    {
      width: imageRegion.window.width,
      height: imageRegion.window.height,
    },
    {
      height: newDimensions.height,
      width: newDimensions.width,
    },
    0,
    imageFit,
    isClampingEnabled
  )

  const { x, y, zoom } = newImageTransformation

  yield put(
    updateOneImageTransformation({
      id: image.imageTransformId,
      changes: {
        translation: { x, y },
        zoom,
        minZoom: zoom,
        rotation: 0,
        dirty: false,
      },
    })
  )

  yield put(
    updateOneUpload({
      id: upload.id,
      changes: {
        isReady: true,
        location: imageAITransformResult.outputUrl,
      },
    })
  )
}
